import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import MapComponent from '../common/MapComponent';
import StoryModal from '../common/StoryModal';
import ConfirmationModal from '../common/ConfirmationModal';
import haversine from 'haversine-distance';
import axios from 'axios';
import { auth } from '../../firebase';
import io from 'socket.io-client';
import { API_URL, SOCKET_URL } from '../../apiConfig';

const PublicDashboard = ({ user, isLoaded, loadError, MAP_ID, setUser }) => {
    const [allRequests, setAllRequests] = useState([]);
    const [completedRequests, setCompletedRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [selectedRequest, setSelectedRequest] = useState(null);
    const [userLocation, setUserLocation] = useState(null);
    const [mapCenter, setMapCenter] = useState({ lat: 24.8607, lng: 67.0011 });
    const dragControls = useDragControls();

    const [map, setMap] = useState(null);
    const [showRecenterButton, setShowRecenterButton] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('All');
    const categories = ['All', 'Food', 'Environment', 'Education', 'Health', 'Animal Welfare', 'Elderly Care', 'Others'];
    const [storyRequest, setStoryRequest] = useState(null);
    
    const [directions, setDirections] = useState(null);
    const [activeRequest, setActiveRequest] = useState(null);
    
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    
    const [impactAlert, setImpactAlert] = useState(null);
    const [liveVolunteer, setLiveVolunteer] = useState(null);

    const notificationSound = useMemo(() => {
        if (typeof window !== 'undefined') {
            return new Audio('/notification.mp3'); 
        }
        return null;
    }, []);

    const fetchData = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true);
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) {
                if (showLoading) setLoading(false);
                return;
            }
            const idToken = await currentUser.getIdToken();

            const openRequestsPromise = axios.get(`${API_URL}/requests`);
            const completedRequestsPromise = axios.get(`${API_URL}/requests/history`, {
                headers: { Authorization: `Bearer ${idToken}` },
            });
            const userProfilePromise = axios.post(`${API_URL}/users/auth`, {}, {
                headers: { Authorization: `Bearer ${idToken}` },
            });

            const [openRequestsResponse, completedRequestsResponse, userProfileResponse] = await Promise.all([
                openRequestsPromise,
                completedRequestsPromise,
                userProfilePromise,
            ]);

            const formattedOpenRequests = openRequestsResponse.data.map(req => ({
                ...req,
                ngo: { name: req.postedBy?.organizationName || 'An NGO', logo: 'https://i.imgur.com/S5t8C2g.png' }
            }));

            setAllRequests(formattedOpenRequests);
            setCompletedRequests(completedRequestsResponse.data);
            setUser(userProfileResponse.data);

        } catch (err) {
            console.error("Error fetching data:", err);
            setError("Failed to load data from the server.");
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [setUser]);

    useEffect(() => {
        const socket = io(SOCKET_URL);

        socket.on('newRequest', (newRequest) => {
            notificationSound?.play().catch(e => console.error("Error playing sound:", e));
            const formattedRequest = {
                ...newRequest,
                isNew: true,
                ngo: { name: newRequest.postedBy?.organizationName || 'An NGO', logo: 'https://i.imgur.com/S5t8C2g.png' }
            };
            setAllRequests(prev => [formattedRequest, ...prev]);
            setTimeout(() => {
                setAllRequests(prev => prev.map(r => r._id === newRequest._id ? { ...r, isNew: false } : r));
            }, 2000);
        });

        socket.on('requestClosed', ({ requestId }) => {
            setAllRequests(prev => prev.filter(req => req._id !== requestId));
        });
        
        socket.on('requestCompleted', ({ requestId }) => {
            if (user?.activeRequest === requestId) {
                setActiveRequest(null);
                setDirections(null);
            }
            fetchData(false);
        });
        
        socket.on('requestCancelled', ({ requestId }) => {
             if (user?.activeRequest === requestId) {
                setActiveRequest(null);
                setDirections(null);
            }
            setAllRequests(prev => prev.filter(req => req._id !== requestId));
        });
        
        socket.on('requestReopened', (reopenedRequest) => {
            const formattedRequest = {
                ...reopenedRequest,
                ngo: { name: reopenedRequest.postedBy?.organizationName || 'An NGO', logo: 'https://i.imgur.com/S5t8C2g.png' }
            };
            setAllRequests(prev => [formattedRequest, ...prev]);
        });

        return () => {
            socket.disconnect();
        };
    }, [notificationSound, fetchData, user]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                const newLocation = { lat: latitude, lng: longitude };
                setUserLocation(newLocation);
                setMapCenter(newLocation);
            },
            () => console.log("User denied location access."),
            { enableHighAccuracy: true }
        );
    }, []);

    const filteredRequests = useMemo(() => {
        return allRequests.filter(request => {
            const matchesCategory = activeFilter === 'All' || request.category === activeFilter;
            const matchesSearch = searchTerm === '' || 
                                  request.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                  request.ngo.name.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesCategory && matchesSearch;
        });
    }, [allRequests, activeFilter, searchTerm]);

    const handleAccept = async () => {
        if (!selectedRequest || !userLocation) return;
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("You must be logged in.");
            const idToken = await currentUser.getIdToken();
            await axios.post(
                `${API_URL}/requests/${selectedRequest._id}/accept`, 
                {},
                { headers: { Authorization: `Bearer ${idToken}` } }
            );

            const directionsService = new window.google.maps.DirectionsService();
            directionsService.route(
                {
                    origin: userLocation,
                    destination: selectedRequest.location,
                    travelMode: window.google.maps.TravelMode.DRIVING,
                },
                (result, status) => {
                    if (status === window.google.maps.DirectionsStatus.OK) {
                        setDirections(result);
                        setActiveRequest({ ...selectedRequest, routeInfo: result.routes[0].legs[0] });
                        setUser(prev => ({ ...prev, activeRequest: selectedRequest._id }));
                    } else {
                        console.error(`error fetching directions ${result}`);
                    }
                }
            );
            setSelectedRequest(null);
        } catch (err) {
            console.error("Error accepting request:", err);
            alert(err.response?.data?.error || "Could not accept the request.");
        }
    };
    
    const confirmCancelAcceptance = async () => {
        if (!user.activeRequest) return;
        setIsCancelModalOpen(false);
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("User not logged in");
            const idToken = await currentUser.getIdToken();
            await axios.post(
                `${API_URL}/requests/${user.activeRequest}/cancel-acceptance`,
                {},
                { headers: { Authorization: `Bearer ${idToken}` } }
            );
            setActiveRequest(null);
            setDirections(null);
            fetchData(false);
        } catch (err) {
            console.error("Error cancelling acceptance:", err);
            alert(err.response?.data?.error || "Could not cancel acceptance.");
        }
    };

    const onMapLoad = useCallback((mapInstance) => {
        setMap(mapInstance);
    }, []);

    const handleMapDrag = () => {
        if (!directions) {
            setShowRecenterButton(true);
        }
    };

    const handleRecenter = () => {
        if (map && userLocation) {
            map.panTo(userLocation);
            setShowRecenterButton(false);
        }
    };

    const sheetVariants = {
        hidden: { y: "100%" },
        visible: { y: "0%" },
        exit: { y: "100%" }
    };
    
    const calculateDistance = (loc1, loc2) => {
        if (!loc1 || !loc2) return 'N/A';
        const distanceMeters = haversine(loc1, loc2);
        return `${(distanceMeters / 1000).toFixed(1)} km`;
    };

    return (
        <div className="h-screen w-full flex flex-col relative overflow-hidden bg-gray-200">
            <StoryModal 
                isOpen={!!storyRequest}
                onClose={() => setStoryRequest(null)}
                request={storyRequest}
            />
            <ConfirmationModal 
                isOpen={isCancelModalOpen}
                onClose={() => setIsCancelModalOpen(false)}
                onConfirm={confirmCancelAcceptance}
                status="confirm"
                reward={{ description: 'cancel your commitment', pointsCost: 0 }}
            />

            {!user?.activeRequest && (
                <motion.div 
                    initial={{ y: -200 }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="absolute top-0 left-0 right-0 z-20 p-4 pt-6"
                >
                    <div className="max-w-xl mx-auto">
                        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg p-2 space-y-2">
                            <input
                                type="text"
                                placeholder="Search for location or NGO..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-transparent px-4 py-2 text-lg text-gray-800 placeholder-gray-500 focus:outline-none"
                            />
                            <div className="flex space-x-2 overflow-x-auto pb-2 px-2">
                                {categories.map(category => (
                                    <button
                                        key={category}
                                        onClick={() => setActiveFilter(category)}
                                        className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                                            activeFilter === category ? 'bg-black text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                    >
                                        {category}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}

            <div className="flex-grow relative">
                <MapComponent 
                    isLoaded={isLoaded}
                    loadError={loadError}
                    requests={filteredRequests}
                    completedRequests={completedRequests}
                    userLocation={userLocation}
                    center={mapCenter}
                    onMarkerClick={setSelectedRequest}
                    onCompletedMarkerClick={setStoryRequest}
                    impactAlert={impactAlert}
                    directions={directions}
                    liveVolunteer={liveVolunteer}
                    MAP_ID={MAP_ID}
                    onLoad={onMapLoad}
                    onDrag={handleMapDrag}
                />
                <AnimatePresence>
                    {showRecenterButton && (
                         <motion.button
                            initial={{ scale: 0, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0, y: 20 }}
                            onClick={handleRecenter}
                            className="absolute bottom-28 right-4 z-10 bg-black text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                            aria-label="Recenter map"
                         >
                            🎯
                         </motion.button>
                    )}
                </AnimatePresence>
            </div>
            
            <AnimatePresence>
                {selectedRequest && (
                    <motion.div
                        key="details-sheet"
                        variants={sheetVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                        drag="y"
                        dragControls={dragControls}
                        dragListener={false}
                        dragConstraints={{ top: 0 }}
                        onDragEnd={(event, info) => {
                            if (info.offset.y > 200) {
                                setSelectedRequest(null);
                            }
                        }}
                        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-20"
                        style={{ maxHeight: '80vh' }}
                    >
                        <div 
                            className="w-full p-4"
                            onPointerDown={(e) => dragControls.start(e)}
                        >
                            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto"></div>
                        </div>

                        <div className="p-6 pt-0 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 50px)' }}>
                            <div className="flex items-center gap-4 mb-4">
                                <img src={selectedRequest.ngo.logo} alt={`${selectedRequest.ngo.name} logo`} className="w-12 h-12 rounded-full border" />
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">{selectedRequest.title}</h2>
                                    <p className="text-gray-600">Posted by {selectedRequest.ngo.name}</p>
                                </div>
                            </div>
                            
                            <p className="text-gray-700 mb-6">{selectedRequest.description}</p>
                            
                            <div className="flex text-center mb-6 border-y py-4">
                                <div className="w-1/2 border-r">
                                    <p className="text-sm text-gray-500">Date</p>
                                    <p className="font-bold text-lg">{new Date(selectedRequest.eventDate).toLocaleDateString()}</p>
                                </div>
                                <div className="w-1/2">
                                    <p className="text-sm text-gray-500">Time</p>
                                    <p className="font-bold text-lg">{selectedRequest.eventTime}</p>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-4 text-center mb-6 bg-gray-50 p-4 rounded-lg">
                                <div>
                                    <p className="text-sm text-gray-500">Distance</p>
                                    <p className="font-bold text-lg">{calculateDistance(userLocation, selectedRequest.location)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Points</p>
                                    <p className="font-bold text-lg">{selectedRequest.points}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Slots Left</p>
                                    <p className="font-bold text-lg">{selectedRequest.peopleNeeded - selectedRequest.volunteersAccepted}</p>
                                </div>
                            </div>
                            
                            <button
                                onClick={handleAccept}
                                className="w-full py-3.5 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all"
                            >
                                Accept & Get Directions
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            
            <AnimatePresence>
                {user?.activeRequest && activeRequest && (
                    <motion.div
                        key="in-ride-panel"
                        initial={{ y: "100%" }}
                        animate={{ y: "0%" }}
                        exit={{ y: "100%" }}
                        transition={{ type: 'spring', stiffness: 400, damping: 40 }}
                        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-30 p-4"
                    >
                        <div className="flex items-center gap-4">
                            <motion.div
                                className="text-5xl"
                                animate={{ rotate: [0, 5, -5, 5, 0] }}
                                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                            >
                               🚗
                            </motion.div>
                            <div className="flex-grow">
                                <p className="text-sm text-gray-500">En route to</p>
                                <h3 className="font-bold text-lg text-gray-900 truncate">{activeRequest.title}</h3>
                                <p className="text-sm text-gray-700 font-semibold">
                                    {activeRequest.routeInfo?.duration.text} ({activeRequest.routeInfo?.distance.text})
                                </p>
                            </div>
                            <button
                                onClick={() => setIsCancelModalOpen(true)}
                                className="px-4 py-2 bg-red-500 text-white font-bold rounded-lg hover:bg-red-600 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PublicDashboard;
